import { StyledTrackingContainer } from '@components/AlternativesOverlay/AlternativeProductCard.styled';
import {
  HighlightedAlternativeCard,
  ProductAlternativeCard
} from '@components/AlternativesOverlay/AlternativeProductCards';
import { Grid, Typography } from '@coolblue-development/freddy';
import addItemTrigger from '@coolblue-development/ga4-events/triggers/CrossSellRecommendations/addItemTrigger';
import shownRecommendationsTrigger from '@coolblue-development/ga4-events/triggers/CrossSellRecommendations/shownRecommendationsTrigger';
import { default as productTypeSelectAlternativeItemTrigger } from '@coolblue-development/ga4-events/triggers/ProductTypeSection/selectAlternativeItemTrigger';
import { default as recommendedSelectAlternativeItemTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/selectAlternativeItemTrigger';
import { TrackWhenVisible, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import { useCustomerContext } from '@features/add-to-cart-page/components/Sections/CustomerContext/CustomerContext';
import {
  AccessorySlot,
  AccessorySlotHighlightedAlternative,
  AccessorySlotProduct
} from '@features/add-to-cart-page/typings/Product';
import { getAddToCartPageType } from '@features/add-to-cart-page/utils/getAddToCartPageType';
import { useTranslation } from '@i18n/useTranslation';
import { ReactElement } from 'react';

export default function AlternativeProductOverlayContent({
  accessorySlot,
  productTypeNamePlural,
  isMediumBreakpointOrUp,
  mainProductId,
  accessoryProductCardIndex,
  accessoryProductCardTotalIndices,
  isAutomated
}: {
  accessorySlot: AccessorySlot;
  productTypeNamePlural?: string;
  isMediumBreakpointOrUp: boolean | undefined;
  mainProductId: number;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
  isAutomated: boolean;
}): ReactElement {
  const { t } = useTranslation('webshop');
  const { trackEvent } = useAnalytics();
  const context = useAccessoryProductCardContext();
  const { isBusinessCustomer } = useCustomerContext();
  const addToCartPageType = getAddToCartPageType({ isBusiness: isBusinessCustomer });

  const assembleAttachedHighlightedAccessoryIds = (): number[] => {
    const attachedItemIds: number[] = [];
    accessorySlot.highlightedAlternative?.slots.forEach(slotProduct => {
      attachedItemIds.push(slotProduct.products[0].product.id);
    });
    return attachedItemIds;
  };

  const trackShownRecommendationParams: {
    primary_item_id: number;
    attached_item_ids: string;
    sub_feature:
      | 'add_to_cart_page_overlay'
      | 'configurator_overlay'
      | 'accessory_checkbox_overlay'
      | 'add_to_cart_overlay_overlay';
  } = {
    primary_item_id: mainProductId,
    attached_item_ids: assembleAttachedHighlightedAccessoryIds().join(','),
    sub_feature: 'add_to_cart_page_overlay'
  };

  const trackSelectAlternativeItem = (
    isAutomated: boolean,
    index: number,
    totalIndices: number,
    itemId: number
  ) => {
    const params = {
      index: index + 1,
      total_indices: totalIndices,
      method: 'image' as const,
      item_id: itemId,
      sub_feature: addToCartPageType
    };

    if (isAutomated) {
      trackEvent(recommendedSelectAlternativeItemTrigger(params));
    } else {
      trackEvent(productTypeSelectAlternativeItemTrigger(params));
    }
  };

  const handleQuantityChange = (quantity?: number) => {
    if (!quantity) return;

    context.setSelectedProductQuantity(quantity);
  };

  return (
    <>
      {accessorySlot.highlightedAlternative &&
        accessorySlot.highlightedAlternative.slots.length > 1 && (
          // Multiple highlighted alternatives
          <TrackWhenVisible trigger={shownRecommendationsTrigger(trackShownRecommendationParams)}>
            <Grid gap={400}>
              {accessorySlot.highlightedAlternative.slots.map((slot, index) => {
                return (
                  <HighlightedAlternativeCard
                    key={slot.products[0].product.id}
                    slot={slot}
                    mainProductId={mainProductId}
                    isMediumBreakpointOrUp={isMediumBreakpointOrUp}
                    accessoryProductCardIndex={accessoryProductCardIndex}
                    accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
                    isAutomated={isAutomated}
                    onClick={quantity => {
                      if (!accessorySlot.highlightedAlternative) return;

                      handleQuantityChange(quantity);

                      trackEvent(
                        addItemTrigger({
                          index: index + 1,
                          total_indices: accessorySlot.highlightedAlternative?.slots.length,
                          sub_feature: 'add_to_cart_page_overlay',
                          item_id: slot.products[0].product.id
                        })
                      );
                    }}
                  />
                );
              })}
            </Grid>
          </TrackWhenVisible>
        )}
      {accessorySlot.products && (
        <>
          {accessorySlot.highlightedAlternative &&
            accessorySlot.highlightedAlternative.slots.length > 1 && (
              <Typography mt={600} mb={400} variant='h3'>
                {t('alternativesOverlay.otherRecommended', { productTypeNamePlural })}
              </Typography>
            )}
          <Grid gap={400}>
            {accessorySlot.highlightedAlternative &&
              accessorySlot.highlightedAlternative.slots.length === 1 && (
                // Single highlighted alternative
                <StyledTrackingContainer>
                  <TrackWhenVisible
                    trigger={shownRecommendationsTrigger(trackShownRecommendationParams)}
                  >
                    <ProductAlternativeCard
                      slot={
                        accessorySlot.highlightedAlternative.slots[0]
                          .products[0] as AccessorySlotProduct
                      }
                      isMediumBreakpointOrUp={isMediumBreakpointOrUp}
                      mainProductId={mainProductId}
                      accessoryProductCardIndex={accessoryProductCardIndex}
                      accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
                      isAutomated={isAutomated}
                      onClick={quantity => {
                        handleQuantityChange(quantity);

                        trackEvent(
                          addItemTrigger({
                            index: 1,
                            total_indices: 1,
                            sub_feature: 'add_to_cart_page_overlay',
                            item_id: (
                              accessorySlot.highlightedAlternative as AccessorySlotHighlightedAlternative
                            ).slots[0].products[0].product.id
                          })
                        );
                      }}
                    />
                  </TrackWhenVisible>
                </StyledTrackingContainer>
              )}
            {accessorySlot.products.map((slot, index) => {
              // Non-highlighted alternatives
              return (
                <ProductAlternativeCard
                  key={slot.product.id}
                  slot={slot}
                  isMediumBreakpointOrUp={isMediumBreakpointOrUp}
                  mainProductId={mainProductId}
                  accessoryProductCardIndex={accessoryProductCardIndex}
                  accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
                  isAutomated={isAutomated}
                  onClick={quantity => {
                    if (!accessorySlot.products) return;

                    handleQuantityChange(quantity);

                    trackSelectAlternativeItem(
                      isAutomated,
                      index,
                      accessorySlot.products.length,
                      slot.product.id
                    );
                  }}
                />
              );
            })}
          </Grid>
        </>
      )}
    </>
  );
}
